﻿import * as React from 'react';

import { parseDateOnly, toDayString, toSnakeCase } from 'utils';
import { Form, FormControlProps } from 'react-bootstrap';

import 'components/GMetrixDatePicker/gmetrix-date-picker.scss';

import { ToolTip } from 'components/ToolTip/ToolTip';

interface GMetrixDatePickerProps extends FormControlProps {
    className?: string;
    infoTitle?: string;
    label?: string;
    max?: string
    min?: string;
    readOnly?: boolean;
    readOnlyNoLock?: boolean;
    required?: boolean;
    value: any;
}

export const GMetrixDatePicker = ({
    className,
    infoTitle,
    label,
    max,
    min,
    readOnly,
    readOnlyNoLock = false,
    required,
    value,
    ...otherProps
}: GMetrixDatePickerProps) => {

    const id = label === undefined ? undefined : `kendo-date-picker-${toSnakeCase(label)}`;

    const checkValidDate = (date: any) => {

        // toDayString reads the literal calendar day — never timezone-shifted.
        const day = toDayString(date);
        return !day || day.startsWith('0001-01-01') ? undefined : day;

    };

    return (
        <div className={className}>
            {label && (
                <div className="d-flex">
                    <Form.Label className="fw-bold" editorId={id}>
                        {label + (required ? '*' : '')}
                        {readOnly && !readOnlyNoLock && <i className="p-1 ms-1 fa-solid fa-lock" />}
                    </Form.Label>

                    {infoTitle && <ToolTip className="mt-1" content={infoTitle} />}
                </div>
            )}
            <Form.Control
                onChange={e => {

                    if (!otherProps.onChange) return;

                    const raw = e.target.value;

                    // A genuinely empty field (cleared via the picker's clear button) — propagate
                    // the clear so consumers can null out the value.
                    if (raw === '') {

                        otherProps.onChange(undefined as any);
                        return;

                    }

                    const validDate = checkValidDate(raw);
                    if (validDate === undefined) return;

                    const [year] = validDate.split('-').map(Number);

                    // While a full 4-digit year is being typed, the native date input reports the
                    // intermediate 1–3 digit years (e.g. 0002 → 0020 → 0202). Emitting those makes
                    // controlled parents validate/reset the field on every keystroke, so the year
                    // becomes impossible to change once a month/day are set. Wait for a full year.
                    if (year < 1000) return;

                    // parseDateOnly anchors the picked day at UTC noon so it survives both
                    // display (UTC reads) and backend date truncation (server-local reads)
                    // without rolling to a neighbouring day — see utils/dates.
                    otherProps.onChange((parseDateOnly(validDate) ?? undefined) as any);

                }}
                disabled={readOnly || readOnlyNoLock}
                id={id}
                max={max}
                min={min}
                onBlur={e => otherProps.onBlur && otherProps.onBlur(e)}
                required={required}
                type="date"
                value={checkValidDate(value)}
            >

            </Form.Control>
        </div>
    );

};
